import { useEffect, useState, useMemo } from 'react'
import { DateTime } from 'luxon'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Accordion, Button, Icon, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { UploadDocumentType } from '@/constants/businessConstants'
import {
  getMissingStatements,
  MissingStatement,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { MissingStatements } from '../missing-bank-statements'
import {
  fetchFinancialAccountsIfNeeded,
  StatementStatusType,
} from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { selectUserStatements } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { EditableStatementTable } from '../EditableStatementTable'

// TODO: migrate to v2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'

export const NoLBAIntroPage = ({
  setShowSkipButton,
}: {
  setShowSkipButton: (showSkipButton: boolean) => void
}) => {
  const dispatch = useAppDispatch()
  setShowSkipButton(true)
  const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false)
  const [financialStatements, setFinancialStatements] = useState<
    MissingStatement[][]
  >([])
  const currentYear = new Date().getFullYear()
  const missingStatements = useFetchResponse(getMissingStatements)
  const statements = useReselector(selectUserStatements)
  const plaidItems = useReselector(selectPlaidItems)
  const plaidStatementAccounts = useMemo(() => {
    return Object.values(plaidItems).filter((item) => {
      return (
        item.accounts[0].statementPermissions ===
        StatementStatusType.PLAID_STATEMENT
      )
    })
  }, [plaidItems])

  useEffect(() => {
    if (missingStatements?.length) {
      const groupedStatements = missingStatements
        .filter((statement) => statement.financial_institution_id !== undefined)
        .reduce(
          (acc, statement) => {
            const { financial_institution_id } = statement

            if (financial_institution_id && !acc[financial_institution_id]) {
              acc[financial_institution_id] = []
            }

            if (financial_institution_id) {
              acc[financial_institution_id].push(statement)
            }

            return acc
          },
          {} as Record<string, typeof missingStatements>
        )
      for (const statement of statements) {
        const financialAccountId =
          statement.statement?.statementFinancialAccounts?.[0]
            ?.financialAccountId
        if (!financialAccountId) continue

        for (const group of Object.values(groupedStatements)) {
          const match = group.find(
            (account) => account.financialAccountId === financialAccountId
          )
          if (match) {
            if (
              match.financial_institution_id &&
              groupedStatements[match.financial_institution_id]
            ) {
              for (const statementGrouped of groupedStatements[
                match.financial_institution_id
              ]) {
                const isoDate = statement.statement?.statementMonth
                  ? DateTime.fromFormat(
                      statement.statement.statementMonth,
                      'yyyy-MM'
                    )
                      .startOf('month')
                      .toISO()
                  : null
                if (isoDate && !statementGrouped.statements.includes(isoDate)) {
                  if (statement.statement?.statementMonth) {
                    statementGrouped.statements.push(isoDate)
                  }
                }
              }
            }
            break // Exit the inner loop but continue processing other statements
          }
        }
      }

      const groupedArray = Object.values(groupedStatements)
      setFinancialStatements(groupedArray)
    }
  }, [missingStatements, statements])
  const shouldEnableAutoReconCUBK2 = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBK2,
    false
  )
  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
    }
    setShowSkipButton(true)
    fetch()
  }, [dispatch, setShowSkipButton])

  const faqAccordions = shouldEnableAutoReconCUBK2 ? (
    <Accordion type="multiple" variant="text">
      <Accordion.Item value="why-statements">
        <Accordion.Trigger>
          Why do you need my bank statements?
        </Accordion.Trigger>
        <Accordion.Content>
          <Text>
            These help us complete your financial record from before you joined
            Heard. This is important for bookkeeping accuracy and compliance.
            This also allows us to:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Provide financial insights and recommendations</li>
              <li>Calculate accurate quarterly tax estimates</li>
              <li>
                Check that all income and expenses are properly accounted for
              </li>
            </ul>
          </Text>
        </Accordion.Content>
      </Accordion.Item>

      <Accordion.Item value="missing-statement">
        <Accordion.Trigger>
          What if I can&apos;t find a statement right now?
        </Accordion.Trigger>
        <Accordion.Content>
          <Text>
            We <b>strongly recommend</b> taking the time to upload all required
            statements. They are needed to fully set up your account, as well as
            for compliance, tax, and bookkeeping purposes.
          </Text>
        </Accordion.Content>
      </Accordion.Item>

      <Accordion.Item value="multi-month-statement">
        <Accordion.Trigger>
          What if I have a statement that covers multiple accounts or months?
        </Accordion.Trigger>
        <Accordion.Content>
          <Text>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                Select <b>“I have a statement covering multiple months”</b>.
              </li>
              <li>
                Upload the statement to “Multi-month statement”. It&apos;s okay
                to upload the same statement for each covered account.
              </li>
            </ol>
          </Text>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  ) : (
    <Accordion type="single" collapsible variant="text">
      <Accordion.Item value="why-statements-alt">
        <Accordion.Trigger>
          Why do you need my bank statements?
        </Accordion.Trigger>
        <Accordion.Content>
          <div className="space-y-4">
            <Text>
              We need your bank statements to get your books set up and
              reconciled. In the future, we&apos;ll reconcile automatically when
              possible and will only need bank statements if there&apos;s a
              Plaid connection error.
            </Text>

            <Text variant={TypographyStyles.H3}>
              But I just connected my bank account?
            </Text>
            <Text>
              Connecting your account doesn&apos;t automatically grant Heard
              access to your bank statements. Common reasons we may not have
              access:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>
                  Your bank doesn&apos;t allow Heard access to your bank
                  statements
                </li>
                <li>
                  You declined access to your statements when connecting your
                  account
                </li>
              </ul>
            </Text>
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  )

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H2}>Upload monthly bank statements</Text>

      {shouldEnableAutoReconCUBK2 ? (
        <Text>
          Finish setting up your account by uploading the monthly bank
          statements below. This is important for compliance and completeness of
          your books.
        </Text>
      ) : (
        <Text>
          Make sure to upload monthly bank statements for{' '}
          <b>all connected business accounts.</b>
        </Text>
      )}

      <Text variant={TypographyStyles.H3}>Instructions</Text>
      <div className="space-y-1">
        <Text>1. Log into each connected business account.</Text>
        <Text>
          2. Download all monthly statements for each month of {currentYear}.
        </Text>
        <Text>3. Upload these statements (JPG, PNG, PDF) here.</Text>
      </div>

      {faqAccordions}

      {!shouldEnableAutoReconCUBK2 && statements?.length > 0 && (
        <EditableStatementTable documents={statements} />
      )}

      <div className="space-y-4">
        {shouldEnableAutoReconCUBK2 &&
          financialStatements?.map((statement) => (
            <MissingStatements
              key={statement[0].financial_institution_id}
              missingStatements={statement}
              statements={statements}
            />
          ))}

        {shouldEnableAutoReconCUBK2 &&
          plaidStatementAccounts?.map((account) => (
            <MissingStatements
              key={account.institutionId}
              missingStatements={[]}
              statements={statements}
              plaidAccount={account}
              isPlaid
            />
          ))}

        {!shouldEnableAutoReconCUBK2 && (
          <Button onClick={() => setUploadFileModalOpen(true)}>
            <Icon icon={regular('upload')} className="mr-2" />
            Upload
          </Button>
        )}
      </div>

      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={uploadFileModalOpen}
        close={() => setUploadFileModalOpen(false)}
        userFacing
        useFilteredAccountOptions
      />
    </div>
  )
}
